import {  ApolloError, gql, useMutation,  } from "@apollo/client";
import { BusinessOnboardingStepTwoMutationMutation, BusinessOnboardingStepTwoMutationMutationVariables } from "../../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../../utils/normalizeErrors";

const LOGO_IMAGE_LIMIT_BYTES = 450 * 1024;
const BANNER_IMAGE_LIMIT_BYTES = 900 * 1024;
const STEP_TWO_IMAGE_TOO_LARGE_ERROR =
  "Image is still too large after compression. Please upload a smaller JPG, PNG, or WebP image.";

type ImageCompressionOptions = {
  maxWidth: number;
  maxHeight: number;
  minWidth: number;
  minHeight: number;
  maxBytes: number;
};

export const businessOnboardingStepTwoMutation = gql`
  mutation BusinessOnboardingStepTwoMutation(
    $username: String!
    $industryId: String!
    $companyLocation: String!
    $logoUrl: String!
    $bannerUrl: String!
    $description: String!
  ) {
    businessOnboardingStepTwo(
      username: $username
      industryId: $industryId
      companyLocation: $companyLocation
      logoUrl: $logoUrl
      bannerUrl: $bannerUrl
      description: $description
    ) {
      errors {
        path
        message
      }
      message
      logoUrl
      bannerUrl
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (data: {
    submit: (
      values: BusinessOnboardingStepTwoMutationMutationVariables 
    ) => Promise<{
      errors: NormalizedErrorMap | null;
      message?: string;
    }>;
    loading: boolean;
    error?: ApolloError;
  }) => React.ReactElement | null;
}

// Utility to convert File to base64 data URL
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function loadImageDimensions(file: File): Promise<{ width: number; height: number; image: HTMLImageElement }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.width, height: image.height, image });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to load image for upload."));
    };

    image.src = objectUrl;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Unable to prepare image upload."));
        return;
      }

      resolve(blob);
    }, type, quality);
  });
}

function getOutputType(file: File): string {
  return file.type === "image/png" ? "image/png" : "image/jpeg";
}

function formatImageCompressionError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Onboarding step two failed. Please try again.";
}

function getScaledDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  const widthRatio = maxWidth / width;
  const heightRatio = maxHeight / height;
  const scale = Math.min(widthRatio, heightRatio, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function compressImageForUpload(
  file: File,
  options: ImageCompressionOptions,
): Promise<string> {
  const { width, height, image } = await loadImageDimensions(file);
  const outputType = getOutputType(file);
  const initialSizeOkay = file.size <= options.maxBytes;
  const dimensionsOkay = width <= options.maxWidth && height <= options.maxHeight;

  if (initialSizeOkay && dimensionsOkay) {
    return fileToBase64(file);
  }

  let { width: targetWidth, height: targetHeight } = getScaledDimensions(
    width,
    height,
    options.maxWidth,
    options.maxHeight,
  );
  let quality = outputType === "image/png" ? undefined : 0.82;
  const minQuality = 0.55;

  while (true) {
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      return fileToBase64(file);
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const blob = await canvasToBlob(canvas, outputType, quality);
    if (blob.size <= options.maxBytes) {
      return blobToDataUrl(blob);
    }

    if (typeof quality === "number" && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.08);
      continue;
    }

    const nextWidth = Math.max(options.minWidth, Math.floor(targetWidth * 0.85));
    const nextHeight = Math.max(options.minHeight, Math.floor(targetHeight * 0.85));

    if (nextWidth === targetWidth && nextHeight === targetHeight) {
      throw new Error(STEP_TWO_IMAGE_TOO_LARGE_ERROR);
    }

    targetWidth = nextWidth;
    targetHeight = nextHeight;
  }
}

export const BusinessOnboardingTwo: React.FC<Props> = ({
  children,
  onMessage,
}) => {
  const [_businessOnboardingStepTwoMutation, { loading, error }] = useMutation<
    BusinessOnboardingStepTwoMutationMutation,
    BusinessOnboardingStepTwoMutationMutationVariables
  >(businessOnboardingStepTwoMutation);

  const submit = async (
    values: BusinessOnboardingStepTwoMutationMutationVariables & {
      logoFile?: File;
      bannerFile?: File;
    }
  ) => {
    try {
      const { logoFile, bannerFile, ...rest } = values;
      let logoUrl = rest.logoUrl;
      let bannerUrl = rest.bannerUrl;

      if (logoFile) {
        logoUrl = await compressImageForUpload(logoFile, {
          maxWidth: 512,
          maxHeight: 512,
          minWidth: 71,
          minHeight: 71,
          maxBytes: LOGO_IMAGE_LIMIT_BYTES,
        });
      }

      if (bannerFile) {
        bannerUrl = await compressImageForUpload(bannerFile, {
          maxWidth: 1280,
          maxHeight: 720,
          minWidth: 313,
          minHeight: 190,
          maxBytes: BANNER_IMAGE_LIMIT_BYTES,
        });
      }

      const { data } = await _businessOnboardingStepTwoMutation({
        variables: {
          ...rest,
          logoUrl,
          bannerUrl,
        },
      });

      const errors = data?.businessOnboardingStepTwo?.errors
        ? normalizeErrors(data.businessOnboardingStepTwo.errors)
        : null;

      if (data?.businessOnboardingStepTwo?.message && onMessage) {
        onMessage(data.businessOnboardingStepTwo.message);
      }

      return {
        errors,
        logoUrl: data?.businessOnboardingStepTwo?.logoUrl ?? logoUrl,
        bannerUrl: data?.businessOnboardingStepTwo?.bannerUrl ?? bannerUrl,
        message: data?.businessOnboardingStepTwo?.message ?? undefined,
      };
    } catch (e: any) {
      return {
        errors: { general: formatImageCompressionError(e) },
        logoUrl: undefined,
        bannerUrl: undefined,
        message: undefined,
      };
    }
  };

  return children({ submit, loading, error });
};